import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { playVoice } from "../../lib/voice";
import { BenchScan } from "./BenchScan";
import { BenchName } from "./BenchName";
import { BenchReview } from "./BenchReview";
import { BenchProgress } from "./BenchProgress";
import { BenchRecent } from "./BenchRecent";
import { SealDialog } from "./SealDialog";

const norm = (u) => u.replace(/\s+/g, "").toUpperCase();
const pad = (n) => String(n).padStart(2, "0");

export const Bench = ({ specimens, target, onSaved, onSeal }) => {
  const [step, setStep] = useState("scan");
  const [uid, setUid] = useState("");
  const [existing, setExisting] = useState(null);
  const [name, setName] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);
  const [sealPrompt, setSealPrompt] = useState(0);
  const prompted = useRef(false);

  const reset = () => { setStep("scan"); setUid(""); setExisting(null); setName(""); setDraft(null); };

  const onScan = useCallback((raw) => {
    const id = norm(raw);
    const ex = specimens.find((s) => s.tag_uid === id) || null;
    setUid(id);
    setExisting(ex);
    setName("");
    if (!ex) setStep("name");
  }, [specimens]);

  const generate = async (extraAvoid = []) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/generate", { name: name.trim(), avoid: extraAvoid });
      setDraft(data);
      setStep("review");
      setTimeout(() => playVoice(data.voice, data.sound_word, data.spore_word), 150);
    } catch (err) {
      toast.error(errMsg(err, "The hive did not answer. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const reprogram = () => { setName(existing.name); setDraft({ ...existing, source: "saved" }); setStep("review"); };

  const save = async () => {
    if (!name.trim() || !draft?.spore_word?.trim()) { toast.error("Object name and hive word are required"); return; }
    setBusy(true);
    const body = {
      tag_uid: uid, name: name.trim(), spore_word: draft.spore_word.trim().toUpperCase(), sound_word: draft.sound_word || "",
      meaning: draft.meaning || "", icon: draft.icon || "Sprout", voice: draft.voice, slot: existing?.slot ?? null,
      memory: draft.memory || {}, riddle: draft.riddle || undefined, sfx_prompt: draft.sfx_prompt || "",
    };
    try {
      const { data } = existing ? await api.put(`/admin/specimens/${existing.id}`, body) : await api.post("/admin/specimens", body);
      toast.success(`#${pad(data.slot)} ${data.name} -> ${data.spore_word}`);
      setRecent((r) => [data, ...r.filter((x) => x.id !== data.id)].slice(0, 8));
      const count = existing ? specimens.length : specimens.length + 1;
      onSaved();
      reset();
      if (count >= target && !prompted.current) { prompted.current = true; setSealPrompt(count); }
    } catch (err) {
      toast.error(errMsg(err, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="bench" className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(0,4fr)]">
      <div className="grid content-start gap-4">
        <BenchScan onScan={onScan} active={step === "scan"} existing={existing} onReprogram={reprogram} onSkip={reset} />
        {step !== "review" && (
          <BenchName uid={uid} name={name} setName={setName} onSubmit={() => generate()} onCancel={reset} busy={busy} active={step === "name"} />
        )}
        {step === "review" && draft && (
          <BenchReview
            draft={draft} setDraft={setDraft} name={name} setName={setName} busy={busy} isUpdate={Boolean(existing)}
            onRegenerate={() => generate([draft.spore_word])} onSave={save} onCancel={reset}
          />
        )}
      </div>
      <div className="grid content-start gap-4">
        <BenchProgress count={specimens.length} target={target} onSeal={onSeal} />
        <BenchRecent items={recent} />
      </div>
      <SealDialog open={sealPrompt > 0} onOpenChange={(v) => !v && setSealPrompt(0)} count={sealPrompt} target={target} onSeal={onSeal} />
    </div>
  );
};
